import api from '@/lib/axios';
import type { CustomerLocation } from '@/contracts/generated/customer-api/data-contracts';
import { CustomerLocationDto } from '@/types';

type Assert<T extends true> = T;
type IsEqual<A, B> =
    (<T>() => T extends A ? 1 : 2) extends
    (<T>() => T extends B ? 1 : 2)
        ? true
        : false;

type HttpErrorLike = {
    status?: number;
    response?: {
        status?: number;
    };
};

const normalizeLocation = (location: CustomerLocation): CustomerLocationDto => {
    const normalized = Object.fromEntries(
        Object.entries(location).map(([key, value]) => [key, value ?? undefined])
    ) as CustomerLocationDto;

    if (!normalized.id || !normalized.name) {
        throw new Error('Customer location response is missing required identity fields.');
    }

    return {
        ...normalized,
        id: normalized.id,
        name: normalized.name,
    };
};

type _NormalizeLocationInputMatchesGeneratedContract = Assert<
    IsEqual<Parameters<typeof normalizeLocation>[0], CustomerLocation>
>;
type _NormalizeLocationOutputMatchesCustomerLocationDto = Assert<
    IsEqual<ReturnType<typeof normalizeLocation>, CustomerLocationDto>
>;

export const getLocations = async (): Promise<CustomerLocationDto[]> => {
    const response = await api.get<CustomerLocation[]>('/customer/locations');
    return response.data.map(normalizeLocation);
};

/**
 * Get featured locations
 * @returns List of featured locations
 */
export const getFeaturedLocations = async (): Promise<CustomerLocationDto[]> => {
    const response = await api.get<CustomerLocation[]>('/customer/locations/featured');
    return response.data.map(normalizeLocation);
};

/**
 * Get a single location by its URL slug
 * @param slug - The URL-friendly slug of the location (e.g., "palma-airport")
 * @returns The location details or null if not found
 */
export const getLocationBySlug = async (slug: string): Promise<CustomerLocationDto | null> => {
    try {
        const response = await api.get<CustomerLocation>(`/customer/locations/by-slug/${slug}`);
        return response.data ? normalizeLocation(response.data) : null;
    } catch (error: unknown) {
        const httpError = error as HttpErrorLike;
        // Handle both raw Axios errors and our custom ApiError
        if (httpError.status === 404 || httpError.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

/**
 * Get a single location by ID (kept for backward compatibility)
 * @param id - The GUID of the location
 */
export const getLocationById = async (id: string): Promise<CustomerLocationDto | null> => {
    try {
        const response = await api.get<CustomerLocation>(`/customer/locations/${id}`);
        return response.data ? normalizeLocation(response.data) : null;
    } catch (error: unknown) {
        const httpError = error as HttpErrorLike;
        // Handle both raw Axios errors and our custom ApiError
        if (httpError.status === 404 || httpError.response?.status === 404) {
            return null;
        }
        throw error;
    }
};
